import {Trans} from '@lingui/macro'
import type React from 'react'
import styled, {css} from 'styled-components'

import {Button, Stack, tokens} from '@pleo-io/telescope'
import {ArrowLeft} from '@pleo-io/telescope-icons'

import {breakpoints} from '@product-web/styles/theme'
import {PleoLogo} from '@product-web/telescope-lab/logo-pleo/logo-pleo'
import {PleoLogoHorizontal} from '@product-web/telescope-lab/logo-pleo-horizontal/logo-pleo-horizontal'
import {useMediaQuery} from '@product-web/web-platform/use-media-query'

function Screen({
    children,
    imgSrc,
    hideImageOnSmallScreen,
    hideLogoOnSmallScreen = true,
    hideBackButton,
    onBack,
    bottomNav,
}: {
    children: React.ReactNode
    imgSrc: string
    hideImageOnSmallScreen: boolean
    hideLogoOnSmallScreen?: boolean
    hideBackButton?: boolean
    onBack?: () => void
    bottomNav?: React.ReactNode
}) {
    const isMobile = useMediaQuery(`(max-width: ${breakpoints.mobileLrgUp})`)
    const isTablet = useMediaQuery(`(max-width: ${breakpoints.tabletUp})`)

    return (
        <Wrapper>
            <ContentWrapper>
                <HeaderWrapper space={16}>
                    {!isMobile && <PleoLogo />}
                    {isMobile && !hideLogoOnSmallScreen && <PleoLogoHorizontal />}
                    {!hideBackButton && onBack && (
                        <Button variant="link" IconLeft={ArrowLeft} onClick={onBack}>
                            <Trans>Back</Trans>
                        </Button>
                    )}
                </HeaderWrapper>
                <Content $hideImageOnSmallScreen={hideImageOnSmallScreen}>
                    {isTablet && !hideImageOnSmallScreen && (
                        <ResponsiveImage $hideImageOnSmallScreen={hideImageOnSmallScreen}>
                            <img src={imgSrc} alt="" />
                        </ResponsiveImage>
                    )}

                    <section>{children}</section>
                </Content>
                {bottomNav && <BottomNav>{bottomNav}</BottomNav>}
            </ContentWrapper>
            {!isTablet && (
                <ColumnImage $hideImageOnSmallScreen={hideImageOnSmallScreen}>
                    <img src={imgSrc} alt="" />
                </ColumnImage>
            )}
        </Wrapper>
    )
}

export default Screen

const Wrapper = styled.section`
    display: grid;
    grid-template-areas: 'content illustration';
    grid-template-columns: 55% 1fr;
    grid-template-rows: auto;
    align-content: stretch;
    min-height: 100%;
    height: 100vh;
    width: 100vw;

    @media (max-width: ${breakpoints.tabletUp}) {
        grid-template-areas:
            'content'
            'illustration';
        grid-template-columns: 1fr;
        grid-template-rows: 100vh 0;
        align-content: stretch;
        overflow: hidden;
    }
`

const ContentWrapper = styled.div`
    display: grid;
    grid-template-areas:
        'logo'
        'content'
        'bottomNav';
    grid-template-columns: 1fr;
    grid-template-rows: 165px 1fr 165px;
    grid-gap: ${tokens.spacing16};
    align-content: stretch;
    overflow: hidden;
`

const Content = styled.div<{$hideImageOnSmallScreen: boolean}>`
    grid-area: content;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    section {
        max-width: 400px;

        @media (max-width: ${breakpoints.tabletUp}) {
            ${(props) =>
                props.$hideImageOnSmallScreen &&
                css`
                    margin-top: 100px; /* stylelint-disable-line */
                `}
        }

        @media (max-width: ${breakpoints.mobileLrgUp}) {
            ${(props) =>
                props.$hideImageOnSmallScreen &&
                css`
                    margin-top: 0;
                    margin-left: ${tokens.spacing16};
                `}
        }
    }
`

export const BottomNav = styled.div`
    padding-left: ${tokens.spacing60};

    @media (max-width: ${breakpoints.mobileLrgUp}) {
        padding-left: ${tokens.spacing20};
    }

    grid-area: bottomNav;
`

const HeaderWrapper = styled(Stack)`
    grid-area: logo;
    margin-left: ${tokens.spacing48};
    margin-top: ${tokens.spacing48};

    @media (max-width: ${breakpoints.mobileLrgUp}) {
        margin-left: ${tokens.spacing24};
        margin-top: ${tokens.spacing48};
    }
`

const ResponsiveImage = styled.div<{$hideImageOnSmallScreen: boolean}>`
    @media (max-width: ${breakpoints.tabletUp}) {
        ${(props) =>
            !props.$hideImageOnSmallScreen &&
            css`
                img {
                    width: 400px;
                }
            `}
    }

    @media (max-width: ${breakpoints.mobileLrgUp}) {
        ${(props) =>
            !props.$hideImageOnSmallScreen &&
            css`
                img {
                    width: 272px;
                }
            `}
    }
`

const ColumnImage = styled.div<{$hideImageOnSmallScreen: boolean}>`
    display: flex;
    align-items: center;
    justify-content: center;
    grid-area: illustration;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;

    img {
        width: 100%;
    }

    @media (max-width: ${breakpoints.tabletUp}) {
        ${(props) =>
            props.$hideImageOnSmallScreen &&
            css`
                display: none;
            `}
    }
`
